import { gql, useMutation } from '@apollo/client';
import { getLanguageDisplayName, getOrgLanguages } from '@assets/i18n/config';

import type { Languages } from '@onevesthq/ov-enums';
import { Dialog, DialogTitle } from '@ovComponents/2-component/dialog/dialog';

import { UserContext } from '@providers/userContextProvider';
import { Box, IconButton, Typography, useTheme } from 'hephaestus/web';
import { Check as CheckIcon, Close as CloseIcon } from 'hephaestus/webIcons';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

const UPDATE_ORGANIZATION_USER_LANGUAGE = gql`
  mutation updateOrganizationUserProfile(
    $input: UpdateOrganizationUserProfileInput!
  ) {
    updateOrganizationUserProfile(input: $input) {
      organizationUser {
        id
        language
      }
    }
  }
`;

export const LanguageSelectionModal = ({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const { t } = useTranslation('components');
  const theme = useTheme();
  const { activeLanguage, setActiveLanguage } = useContext(UserContext);
  const languages = getOrgLanguages();

  const [updateOrganizationUserLanguage] = useMutation(
    UPDATE_ORGANIZATION_USER_LANGUAGE,
  );

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth='xs' fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          borderBottom: `1px ${theme.color.primaryContainer} solid`,
        }}
      >
        <Typography variant='headingSmall'>
          {t('languageSelection.language')}
        </Typography>
        <IconButton
          aria-label='close'
          size='xsmall'
          onClick={() => setOpen(false)}
        >
          <CloseIcon sx={{ color: theme.color.onSurface }} fontSize='inherit' />
        </IconButton>
      </DialogTitle>
      {languages.map((lang: Languages) => (
        <Box
          key={lang}
          sx={{
            paddingY: 2.5,
            paddingX: 3,
            display: 'flex',
            justifyContent: 'space-between',
            backgroundColor:
              activeLanguage === lang
                ? `${theme.color.primaryContainer}`
                : undefined,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: theme.color.surfaceContainer,
            },
          }}
          onClick={() => {
            updateOrganizationUserLanguage({
              variables: {
                input: {
                  language: lang,
                },
              },
              onCompleted: (data: any) => {
                setActiveLanguage(
                  data.updateOrganizationUserProfile.organizationUser.language,
                );
                setOpen(false);
              },
            });
          }}
        >
          <Typography variant='bodyLarge' sx={{ fontWeight: 500 }}>
            {getLanguageDisplayName(lang)}
          </Typography>
          {activeLanguage === lang && <CheckIcon />}
        </Box>
      ))}
    </Dialog>
  );
};
